import { useNavigate } from "react-router";

import { Stack, Heading, SimpleGrid, HStack } from "@chakra-ui/react";

import { Button } from "@/codegen/ui/button";
import { DataListItem, DataListRoot } from "@/codegen/ui/data-list";
import { LegalEntityName } from "@/components/2/legal-entity";
import { usePermerRequirements } from "@/hooks/useHasPermission";
import { EnrichedHohishType } from "@/services/2/hohishes/types";

import { useEnrichedHohishOutletContext } from "../local-context";

interface FieldType {
  field: keyof EnrichedHohishType;
  valueGenerator?: (data: EnrichedHohishType) => React.ReactNode;
  label: string;
  skipIfNullish?: boolean;
}

const identityFields: FieldType[] = [
  { field: "first_name", label: "Prénom" },
  { field: "last_name", label: "Nom" },
  { field: "primary_mail_address", label: "Adresse mail" },
  {
    field: "legal_entity_uuid",
    label: "Entité légale opérationnelle",
    valueGenerator: data => <LegalEntityName legalEntityUuid={data.legal_entity_uuid} />,
  },
  { field: "primary_mail_domain", label: "Domaine mail" },
];

const idsFields: FieldType[] = [
  { field: "uuid", label: "ID DSIT" },
  { field: "microsoft_id", label: "ID Microsoft" },
  { field: "google_id", label: "ID Google" },
  { field: "slack_id", label: "ID Slack" },
  { field: "aircall_id", label: "ID Aircall" },
];

const CustomDataList = ({ fields, data }: { fields: FieldType[]; data: EnrichedHohishType }) => (
  <DataListRoot gap={2} variant={"bold"}>
    {fields.map(({ field, label, valueGenerator, skipIfNullish }) =>
      skipIfNullish && field && !data[field] ? null : (
        <DataListItem
          key={field}
          label={label}
          value={valueGenerator ? valueGenerator(data) : data[field]}
        />
      )
    )}
  </DataListRoot>
);

export const IdentityHohish = () => {
  const hohish = useEnrichedHohishOutletContext();
  const navigate = useNavigate();
  const canEditIdentity = usePermerRequirements().hasDsitUserEditAdvanced();

  return (
    <Stack gap={4}>
      <SimpleGrid columns={2} gap={8} minChildWidth={"sm"}>
        <Stack gap={2}>
          <HStack gap={2}>
            <Heading size="md">Identité</Heading>
            {canEditIdentity && (
              <Button size={"2xs"} onClick={() => navigate("edit")} variant={"ghost"}>
                Modifier
              </Button>
            )}
          </HStack>

          <CustomDataList fields={identityFields} data={hohish} />
        </Stack>

        <Stack gap={2}>
          <Heading size="md">Identifiants</Heading>

          <CustomDataList fields={idsFields} data={hohish} />
        </Stack>
      </SimpleGrid>
    </Stack>
  );
};
